import { useCallback, useEffect, useMemo, useRef } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useParams } from "react-router-dom";

import LoadingButton from "@mui/lab/LoadingButton";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormLabel from "@mui/material/FormLabel";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";

import { merge } from "lodash";

import { useAppSelector } from "app/hooks";
import { DEFAULT_LABEL_CONFIG } from "app/themes/components/input";
import { AssignedTaskTags } from "components/assigned-tags";
import CircularProgress from "components/circular-progress";
import SelectProvider from "components/custom-filters-list/filters/select-provider.component";
import DateStringFormField from "components/date-string-form-field";
import {
  DEFAULT_DATE_QUICK_OPTIONS,
  defaultOnQuickOptionClick
} from "components/date-string-form-field/date-string-form-field.component";
import InputFormField from "components/input-form-field";
import PatientFieldWithSearch from "components/patient-field-with-search";
import type { StatusTransitionCode } from "components/status-transition-cell/status-transition-cell.component";
import ToggleButtonGroupFormField from "components/toggle-button-group-form-field/toggle-button-group-form-field.component";
import WordProcessorField from "components/word-processor-field";
import { TASK_PRIORITY_OPTIONS } from "constants/staff-tasks";
import { TASK_TEMPLATE_TYPES } from "constants/templates";
import { selectCurrentUser } from "features/auth/auth-slice";
import { PATIENT_HEADER_QUERY } from "features/patients/use-patient.hook";
import TaskStatusField from "features/staff-tasks-v2/drawer/staff-task-card/task-status-field-component";
import useStaffTasksV2 from "features/staff-tasks-v2/hooks/use-staff-tasks-v2.hook";
import { useGetPatientQuery } from "services/patients";
import type { StaffTask } from "services/types/staff-tasks.type";

import {
  EMPTY_DEFAULT_TASK_VALUES,
  type StaffTaskV2FormValues,
  getInitialValues
} from "../../../staff-task.utils";
import StaffTaskAttachments, {
  type StaffTaskAttachmentsRef
} from "../staff-task-attachments";

type EditStaffTaskFormProps = {
  task: StaffTask | undefined;
  onSaveCallback?: () => void;
};

export default function EditStaffTaskForm({
  task,
  onSaveCallback
}: EditStaffTaskFormProps) {
  const params = useParams();
  const currentUser = useAppSelector(selectCurrentUser);
  const attachmentsRef = useRef<StaffTaskAttachmentsRef>(null);

  const {
    isPatientViewMode,
    taskModalMode,
    patientId,
    handleUpdate,
    handleCreate,
    isUpdating,
    handleCloseTaskModal,
    handleOpenTaskModal
  } = useStaffTasksV2();

  const isNew = taskModalMode.displayMode === "create";

  const patientIdParams = Number(
    (params.patientId || String(params["*"] ?? "").split("/")[0]) ?? ""
  );

  const fetchPatientId = isNew ? patientId || patientIdParams : patientId;

  const { data: initialPatient } = useGetPatientQuery(
    { patientId: Number(fetchPatientId), query: PATIENT_HEADER_QUERY },
    {
      skip: !fetchPatientId || fetchPatientId === -1
    }
  );

  const initialValues: StaffTaskV2FormValues = useMemo(
    () => getInitialValues(task, currentUser),
    [task, currentUser]
  );

  const defaultValues: StaffTaskV2FormValues = useMemo(() => {
    return merge({}, EMPTY_DEFAULT_TASK_VALUES, initialValues);
  }, [initialValues]);

  const formMethods = useForm<StaffTaskV2FormValues>({
    mode: "onBlur",
    defaultValues
  });

  const {
    control,
    reset,
    setValue,
    handleSubmit,
    watch,
    formState: { isValid }
  } = formMethods;

  const assignee = useWatch({ control, name: "assignee" });

  const [status, patientField]: [StatusTransitionCode, any] = watch([
    "status",
    "patient"
  ]);

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset, isNew]);

  useEffect(() => {
    if (isNew) {
      setValue("patient", initialPatient);
    }
  }, [setValue, initialPatient, taskModalMode.displayMode, isNew]);

  const handleStatusChange = useCallback(
    (code: StatusTransitionCode) => {
      setValue("status", code);
      return new Promise((resolve, reject) => {
        resolve(code);
      });
    },
    [setValue]
  );

  const onSubmit = useCallback(
    (data: StaffTaskV2FormValues) => {
      if (data.id) {
        handleUpdate(data, onSaveCallback);
        if (taskModalMode.onEditCancelMode === "openViewModal") {
          handleOpenTaskModal({ mode: "view", taskId: data.id });
        } else {
          handleCloseTaskModal();
        }
      } else {
        handleCreate(data, async (createdTaskId) => {
          if (createdTaskId && attachmentsRef.current?.hasPendingFiles()) {
            await attachmentsRef.current.uploadPendingFiles(createdTaskId);
          }
        });
        handleCloseTaskModal();
      }
    },
    [
      handleCloseTaskModal,
      handleCreate,
      handleOpenTaskModal,
      handleUpdate,
      onSaveCallback,
      taskModalMode.onEditCancelMode
    ]
  );

  if (taskModalMode.displayMode === "edit" && !task) {
    return <CircularProgress sx={{ width: "100%" }} />;
  }

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)}>
      <Grid container>
        <Grid item xs={4}>
          <Stack rowGap={2}>
            <ToggleButtonGroupFormField
              name="priority"
              label="Priority"
              options={TASK_PRIORITY_OPTIONS}
              control={control}
              required
            />
            <DateStringFormField
              name="due_at"
              label="Due Date"
              control={control}
              PopperProps={{
                style: { zIndex: 14001 }
              }}
              quickOptions={DEFAULT_DATE_QUICK_OPTIONS}
              onQuickOptionClick={defaultOnQuickOptionClick}
              sx={{
                ml: 0,
                pr: 0,
                minWidth: "100%",
                "& > div": { flexGrow: 1 }
              }}
            />
            <Stack rowGap={0.8}>
              <FormLabel sx={DEFAULT_LABEL_CONFIG} id="status">
                Status *
              </FormLabel>
              <TaskStatusField value={status} onChange={handleStatusChange} />
            </Stack>

            {task && <AssignedTaskTags task={task} readonly />}
          </Stack>
        </Grid>
        <Grid item xs={8} sx={{ boxSizing: "border-box", pl: 5 }}>
          <Stack rowGap={2}>
            <InputFormField
              name="title"
              label="Title"
              control={control}
              required
              autoFocus
              sx={{ ml: 0 }}
            />
            <SelectProvider
              isActive={true}
              name="assignee"
              label="Assignee"
              includeOtherClinics
              ignoreRegionsFilter
              ignoreClinicsFilter
              control={control}
              required
              sx={{ ml: 0 }}
            />
            <SelectProvider
              isActive
              name="assignees"
              label="Additional Assignees"
              includeOtherClinics
              ignoreRegionsFilter
              ignoreClinicsFilter
              control={control}
              multiple
              enableSelectAll
              excludeSelectAllValues={assignee?.value}
              sx={{ ml: 0 }}
            />
            <PatientFieldWithSearch
              label="Patient"
              control={control}
              required={isPatientViewMode}
            />
          </Stack>
        </Grid>
      </Grid>
      <Box width="100%" sx={{ mt: 2 }}>
        <WordProcessorField
          name="description"
          label="Description"
          control={control}
          placeholder="Enter task details"
          height={210}
          featuresGroup="textOnly"
          allowTemplates
          allowMergeTags
          allowInsertMergeTagsValues
          templateTypes={TASK_TEMPLATE_TYPES}
          allowToolbarAutohide={false}
          allowHtml={false}
          allowPasteAsText={false}
          mergeTagsPatientId={
            Number(patientField?.value) || initialPatient?.id || -1
          }
        />
      </Box>
      <Box sx={{ mt: 2 }}>
        <StaffTaskAttachments
          ref={attachmentsRef}
          taskId={task?.id}
          attachments={task?.attachments}
        />
      </Box>
      <Stack
        sx={{ mt: 2, pr: 3 }}
        justifyContent="flex-end"
        spacing={2}
        direction="row"
      >
        <Button
          variant="outlined"
          size="small"
          onClick={() => {
            if (
              task?.id &&
              taskModalMode.onEditCancelMode === "openViewModal"
            ) {
              handleOpenTaskModal({ mode: "view", taskId: task.id });
            } else {
              handleCloseTaskModal();
            }
          }}
        >
          Cancel
        </Button>
        <LoadingButton
          disabled={!isValid}
          loading={isUpdating}
          variant="contained"
          size="small"
          type="submit"
        >
          Save
        </LoadingButton>
      </Stack>
    </Box>
  );
}
